import { z } from 'zod';

import { resolveUrlChannel } from '@/shared-survey';
import { Channel } from '@/survey-graphql';

/**
 * Mirrors legacy's `useResponderEmail`
 * (`project-response-insight-info.hooks.ts`) rather than importing it: that
 * hook is not on `@/shared-project-response`'s barrel, and that library is
 * byte-frozen for this PR, so adding an export there is not available. The
 * semantics are copied deliberately and must not drift — a prefilled link's
 * `?email=` is how an emailed response gets attributed to a contact.
 *
 * An invalid or absent address resolves to `undefined`, never an empty string:
 * `initiateUnifiedSession` treats a missing email as "anonymous responder", and
 * `''` would create a contact keyed on nothing.
 */
export function parseResponderEmail(
  rawEmail: string | null
): string | undefined {
  const parsedEmail = z.email().safeParse(rawEmail);

  return parsedEmail.success ? parsedEmail.data : undefined;
}

/**
 * Mirrors legacy's `useChannel`, including its two quirks: the panel arrives
 * under a URL-only alias that is not a `Channel` value, and anything
 * unrecognised — including a missing param — falls back rather than erroring,
 * so a hand-edited link still records a response.
 *
 * `fallback` is the channel the route implies; it must be the same one the
 * server resolved for `hasScreener`, because the screener session and
 * `initiateUnifiedSession` both spend it — earning a key on one channel and
 * being asked for one on another is an unrecoverable loop.
 */
export function resolveResponderChannel(
  rawChannel: string | null,
  fallback: Channel = Channel.Link
): Channel {
  return resolveUrlChannel(rawChannel) ?? fallback;
}
